import { isMediaMotionActive, onMediaMotionSettle } from "./media-motion";

/** Warm roughly the next screen and the adjacent carousel cards, never the whole feed. */
const VERTICAL_LOOKAHEAD = 1000;
const HORIZONTAL_LOOKAHEAD = 400;
/**
 * A player already inside the window keeps its source until it is this much
 * further out. Without the gap, a card parked on the boundary (a carousel
 * settling a few px either side of it) attached and released on alternate
 * frames — a fresh playlist fetch and decoder setup each time.
 */
const LEAVE_SCALE = 1.5;
/**
 * The nearest videos get a real buffer rather than just metadata, so the one
 * the user swipes or scrolls to next starts on an already-decoded frame. Three
 * covers the card on screen plus one neighbour either way.
 */
export const HOT_VIDEOS = 3;
/**
 * Ceiling on players holding a source at once, however many fall inside the
 * lookahead. iOS stalls media elements over its concurrent-load limit without
 * an error; a hub page stacks enough carousels to reach it. A player already
 * attached keeps its source until it drops past `ATTACHED_LEAVE`, for the same
 * reason as `LEAVE_SCALE`.
 */
const MAX_ATTACHED = 6;
const ATTACHED_LEAVE = 8;

/**
 * - `cold`: outside the budget — hold no source.
 * - `warm`: attach the source and metadata only.
 * - `hot`: buffer enough to start instantly.
 */
export type VideoWarmth = "cold" | "warm" | "hot";

type Rect = Pick<
  DOMRectReadOnly,
  "top" | "right" | "bottom" | "left" | "width" | "height"
>;

export function isVideoNearby(
  rect: Rect,
  viewport: Rect,
  clips: Rect[] = [],
  scale = 1,
) {
  if (rect.width <= 0 || rect.height <= 0) return false;
  const vertical = VERTICAL_LOOKAHEAD * scale;
  const horizontal = HORIZONTAL_LOOKAHEAD * scale;
  return [viewport, ...clips].every(
    (clip) =>
      clip.width > 0 &&
      clip.height > 0 &&
      rect.bottom > clip.top - vertical &&
      rect.top < clip.bottom + vertical &&
      rect.right > clip.left - horizontal &&
      rect.left < clip.right + horizontal,
  );
}

/**
 * How far `rect` sits outside the region it can be seen through (the viewport
 * and every clipping carousel); 0 once any part of it is on screen. This ranks
 * a carousel's peeking neighbour ahead of an off-screen card in the next row,
 * which a centre-to-centre distance gets backwards.
 */
export function visibilityGap(rect: Rect, viewport: Rect, clips: Rect[] = []) {
  let gap = 0;
  for (const clip of [viewport, ...clips]) {
    const dx = Math.max(clip.left - rect.right, rect.left - clip.right, 0);
    const dy = Math.max(clip.top - rect.bottom, rect.top - clip.bottom, 0);
    gap = Math.max(gap, Math.hypot(dx, dy));
  }
  return gap;
}

type MediaWatch = {
  notify: (nearby: boolean) => void;
  nearby?: boolean;
  clips: Element[];
};
type VideoWatch = {
  notify: (warmth: VideoWarmth) => void;
  warmth?: VideoWarmth;
  clips: Element[];
};
const mediaWatches = new Map<Element, MediaWatch>();
const videoWatches = new Map<HTMLVideoElement, VideoWatch>();
let frame: number | undefined;
let observer: IntersectionObserver | undefined;
let stopSettleWatch: (() => void) | undefined;

const watchCount = () => mediaWatches.size + videoWatches.size;

function update() {
  frame = undefined;
  // A hidden document keeps what it has. Treating it as "far" released every
  // player on each app switch, so the video on screen cold-started again on
  // return; the autoplay provider already pauses playback while hidden.
  if (document.hidden) return;
  // Mid-swipe: measure and re-rank once the carousel settles, not per frame.
  if (isMediaMotionActive()) return;
  const viewport = {
    top: 0,
    left: 0,
    right: innerWidth,
    bottom: innerHeight,
    width: innerWidth,
    height: innerHeight,
  };
  // Read all geometry before notifying players, which can change DOM media state.
  const measure = (element: Element, clips: Element[]) => ({
    rect: element.getBoundingClientRect(),
    clips: clips.map((clip) => clip.getBoundingClientRect()),
  });
  const mediaChanges = [...mediaWatches].map(([element, watch]) => {
    const { rect, clips } = measure(element, watch.clips);
    return { watch, nearby: isVideoNearby(rect, viewport, clips) };
  });
  const ranked = [...videoWatches]
    .map(([video, watch]) => {
      const { rect, clips } = measure(video, watch.clips);
      const attached = watch.warmth === "warm" || watch.warmth === "hot";
      return {
        watch,
        attached,
        nearby: isVideoNearby(
          rect,
          viewport,
          clips,
          attached ? LEAVE_SCALE : 1,
        ),
        gap: visibilityGap(rect, viewport, clips),
        centre: Math.hypot(
          rect.left + rect.width / 2 - viewport.width / 2,
          rect.top + rect.height / 2 - viewport.height / 2,
        ),
      };
    })
    .filter((candidate) => candidate.nearby)
    .sort((a, b) => a.gap - b.gap || a.centre - b.centre);
  const warmth = new Map<VideoWatch, VideoWarmth>();
  ranked.forEach(({ watch, attached }, rank) => {
    warmth.set(
      watch,
      rank < HOT_VIDEOS
        ? "hot"
        : rank < (attached ? ATTACHED_LEAVE : MAX_ATTACHED)
          ? "warm"
          : "cold",
    );
  });
  for (const { watch, nearby } of mediaChanges) {
    if (watch.nearby === nearby) continue;
    watch.nearby = nearby;
    watch.notify(nearby);
  }
  for (const watch of videoWatches.values()) {
    const next = warmth.get(watch) ?? "cold";
    if (watch.warmth === next) continue;
    watch.warmth = next;
    watch.notify(next);
  }
}

/**
 * Embla moves slides with transforms, so native scroll/IntersectionObserver
 * alone miss neighbours. The carousel calls this when it settles; see
 * `media-motion.ts` for why not on every frame of the swipe.
 */
export function refreshVideoProximity() {
  if (watchCount() && frame === undefined)
    frame = requestAnimationFrame(update);
}

/**
 * Promotes every player inside `containers` to hot, measuring nothing.
 *
 * For a carousel in motion: it knows which slides it is heading for, so the
 * card being flung in and its neighbours get their sources before they land
 * — a second swipe started mid-glide otherwise reached a card nobody had
 * warmed. Nothing is demoted here; the budget may run a player or two over
 * until the pass at rest reconciles it.
 */
export function warmVideosIn(containers: Element[]) {
  if (!containers.length) return;
  for (const [video, watch] of videoWatches) {
    if (watch.warmth === "hot") continue;
    if (!containers.some((container) => container.contains(video))) continue;
    watch.warmth = "hot";
    watch.notify("hot");
  }
}

function clipsOf(element: Element) {
  const clips: Element[] = [];
  for (
    let parent = element.parentElement;
    parent;
    parent = parent.parentElement
  ) {
    if (
      parent.matches('[data-slot="carousel-content"]') ||
      /^(auto|scroll)$/.test(getComputedStyle(parent).overflowX)
    )
      clips.push(parent);
  }
  return clips;
}

function track(element: Element, forget: () => void) {
  if (!observer) {
    window.addEventListener("scroll", refreshVideoProximity, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", refreshVideoProximity);
    document.addEventListener("visibilitychange", refreshVideoProximity);
    stopSettleWatch = onMediaMotionSettle(refreshVideoProximity);
    observer = new IntersectionObserver(refreshVideoProximity, {
      rootMargin: "1000px 400px",
    });
  }
  observer?.observe(element);
  refreshVideoProximity();
  return () => {
    observer?.unobserve(element);
    forget();
    if (watchCount()) {
      // A slot in the budget just opened up for the next-nearest player.
      refreshVideoProximity();
      return;
    }
    observer?.disconnect();
    observer = undefined;
    if (frame !== undefined) cancelAnimationFrame(frame);
    frame = undefined;
    window.removeEventListener("scroll", refreshVideoProximity, true);
    window.removeEventListener("resize", refreshVideoProximity);
    document.removeEventListener("visibilitychange", refreshVideoProximity);
    stopSettleWatch?.();
    stopSettleWatch = undefined;
  };
}

/**
 * Ranks every watched video by how close it is to being seen and hands each
 * one its tier. Budgeted across the whole page, not per carousel.
 */
export function observeVideoProximity(
  video: HTMLVideoElement,
  notify: VideoWatch["notify"],
) {
  videoWatches.set(video, { notify, clips: clipsOf(video) });
  return track(video, () => videoWatches.delete(video));
}

/** Images use the same carousel-aware lookahead as videos, without the budget. */
export function observeMediaProximity(
  element: Element,
  notify: MediaWatch["notify"],
) {
  mediaWatches.set(element, { notify, clips: clipsOf(element) });
  return track(element, () => mediaWatches.delete(element));
}
